import { hitApi, ObservableAjax } from '__common/utils/api';
import { LiteProjectActionTypes } from 'actionsConstants';
import {
  CREATE_LITE_PROJECT_SUCCESS,
  CREATE_LITE_PROJECT_FAILURE,
  FETCH_PROJECT_AFTER_LITE_CREATE,
  UPDATE_LITE_PROJECT_SUCCESS,
  UPDATE_LITE_PROJECT_FAILURE,
  FETCH_BOM_PART_LIST_SUCCESS,
  FETCH_BOM_PART_LIST_ERROR,
  FETCH_LITE_BOM_REQUEST,
  buildLiteUpdatePayload,
  SAVE_USER_PROFILE_SUCCESS,
  SAVE_USER_PROFILE_FAILURE,
  UPDATE_USER_PROFILE_SUCCESS,
  UPDATE_USER_PROFILE_FAILURE,
  DELETE_USER_PROFILE_SUCCESS,
  DELETE_USER_PROFILE_FAILURE,
  FETCH_PROJECT_VARIABLES_SUCCESS,
  FETCH_PROJECT_VARIABLES_FAILURE,
  FETCH_USER_PROFILES_SUCCESS,
  FETCH_USER_PROFILES_FAILURE,
  SAVE_LITE_BOM_SUCCESS,
  SAVE_LITE_BOM_FAILURE,
  SET_BOM_PRODUCT,
  EMPTY_APP_ACTION,
} from 'actions';
import { REDIRECT_TO_LITE_PROJECT } from './liteProjectActions';
import { getProductName, isSMTiltPR, isMetalX, isSunframeMicroRail, isSFMFamily, isRM10Evolution, isRmGridflex10D, isRmGridflex5Rwdi, isNxtHorizon, isSolarMount, isCommercialProduct } from '__common/constants/products';
import { state } from '__common/store';
import { BOM_RESET } from 'bom/bomActions';
import {
  getWindSpeedLimits,
  getSnowLoadLimits,
  getBuildingHeightLimits,
  getShortestBuildingLengthLimits,
  getRafterSpacingLimits,
  getSetbackLimits,
} from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/utils/validateProject';
import { validateZipCode } from 'projectDesign/components/projectConfiguration/utils/validation';
import { isASCE722 } from '__common/constants/buildingCodes';
import { RiskCategory } from 'projectDesign/components/projectConfiguration/fields/riskCategory';
import { isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';
import { Observable } from 'rxjs/Observable';

function getPreferredSpanLimits(productId: number | undefined): { min: number; max: number } {
  if (!productId) return { min: 4, max: 144 };
  if (isSFMFamily(productId) || isMetalX(productId)) {
    return { min: 12, max: 72 };
  }
  if (isSMTiltPR(productId)) {
    return { min: 4, max: 72 };
  }
  return { min: 4, max: 144 };
}

export function hasLeftPanelValidationErrors(appState: any): boolean {
  const pc = appState.projectConfiguration || {};
  const env = pc.projectEnvConfig || {};
  const productId = pc.productId;
  const inputUnit = env.input_unit;

  const zipcode = env.zipcode;
  const buildingCode = env.building_code;
  const riskCategory = env.risk_category;
  const windSpeed = env.wind_speed;
  const snowLoad = env.snow_load;
  const seismicSs = env.seismic_ss;
  const seismicS1 = env.seismic_s1;
  const seismicSds = env.seismic_sds;
  const tornadoSpeed = env.tornado_speed;

  if (zipcode !== undefined && zipcode !== null && zipcode !== '') {
    if (!validateZipCode(zipcode)) {
      return true;
    }
  }

  if (windSpeed !== undefined && windSpeed !== null) {
    const limits = getWindSpeedLimits();
    if (windSpeed < limits.lowerLimit || windSpeed > limits.upperLimit) {
      return true;
    }
  }

  if (snowLoad !== undefined && snowLoad !== null) {
    const limits = getSnowLoadLimits();
    if (snowLoad < limits.lowerLimit || snowLoad > limits.upperLimit) {
      return true;
    }
  }

  if (seismicSs !== undefined && seismicSs !== null) {
    if (seismicSs <= 0 || seismicSs > 3.1) {
      return true;
    }
  }

  if (seismicS1 !== undefined && seismicS1 !== null) {
    if (seismicS1 < 0 || seismicS1 > 3.1) {
      return true;
    }
  }

  if (isASCE722(buildingCode || 0) && seismicSds !== undefined && seismicSds !== null) {
    if (seismicSds < 0.01 || seismicSds > 3.1) {
      return true;
    }
  }

  if (isASCE722(buildingCode || 0) && (riskCategory === RiskCategory.III || riskCategory === RiskCategory.IV) && tornadoSpeed !== undefined && tornadoSpeed !== null) {
    const isMetric = isMetricUnit(inputUnit);
    const maxTornado = isMetric ? 222 : 138;
    if (tornadoSpeed < 0 || tornadoSpeed > maxTornado) {
      return true;
    }
  }

  const buildingHeight = env.building_height;
  const shortestBuildingLength = env.shortest_building_length;
  const longestBuildingLength = env.building_length;
  const preferredSpan = env.preferred_span;
  const rafterSpacingInches = env.rafter_spacing_inches;
  const parapetHeight = env.parapet_height_input;
  const setbackDistance = env.setback_distance;
  const blockWeight = env.block_weight;

  if (buildingHeight !== undefined && buildingHeight !== null) {
    const limits = getBuildingHeightLimits();
    if (buildingHeight < limits.lowerLimit || buildingHeight > limits.upperLimit) {
      return true;
    }
  }

  if (shortestBuildingLength !== undefined && shortestBuildingLength !== null) {
    const limits = getShortestBuildingLengthLimits();
    if (shortestBuildingLength < limits.lowerLimit || shortestBuildingLength > limits.upperLimit) {
      return true;
    }
  }

  if (longestBuildingLength !== undefined && longestBuildingLength !== null && productId && isCommercialProduct(productId)) {
    const limits = getShortestBuildingLengthLimits();
    if (longestBuildingLength < limits.lowerLimit || longestBuildingLength > limits.upperLimit) {
      return true;
    }
    if (shortestBuildingLength !== undefined && shortestBuildingLength !== null && longestBuildingLength < shortestBuildingLength) {
      return true;
    }
  }

  const isPitchedProduct = productId && (isNxtHorizon(productId) || isSolarMount(productId));

  if (preferredSpan === undefined || preferredSpan === null) {
    if (isPitchedProduct) {
      return true;
    }
  } else {
    const limits = getPreferredSpanLimits(productId);
    if (preferredSpan < limits.min || preferredSpan > limits.max) {
      return true;
    }
    if (productId && rafterSpacingInches && preferredSpan) {
      if (!(isSMTiltPR(productId) || isMetalX(productId) || isSunframeMicroRail(productId))) {
        if (preferredSpan % rafterSpacingInches !== 0) {
          return true;
        }
      }
    }
  }

  if (rafterSpacingInches === undefined || rafterSpacingInches === null) {
    if (isPitchedProduct) {
      return true;
    }
  } else {
    const limits = getRafterSpacingLimits();
    if (rafterSpacingInches < limits.lowerLimit || rafterSpacingInches > limits.upperLimit) {
      return true;
    }
  }

  if (parapetHeight !== undefined && parapetHeight !== null && parapetHeight !== '') {
    const val = Number(parapetHeight);
    if (!isNaN(val)) {
      if (val < 0 || val > 100) {
        return true;
      }
    }
  }

  if (setbackDistance !== undefined && setbackDistance !== null) {
    const limits = getSetbackLimits();
    if (setbackDistance < limits.lowerLimit || setbackDistance > limits.upperLimit) {
      return true;
    }
    if (buildingHeight !== undefined && buildingHeight !== null && setbackDistance >= buildingHeight / 2) {
      return true;
    }
  }

  if (blockWeight !== undefined && blockWeight !== null) {
    if (blockWeight < 1 || blockWeight > 100) {
      return true;
    }
  }

  return false;
}

// Project-wide panel count. Active roof lives in `panels.panels`; other roofs'
// counts sit on `drawingManager.roofs[id].panels`. Used to skip the autosave-
// chained BOM GET when the project has zero panels (fresh project / post-
// Clear-All) so the right panel stays empty instead of showing zeroed rows.
function countLitePanels(s: any): number {
  const roofs = (s && s.drawingManager && s.drawingManager.roofs) || {};
  const selectedRoofId = s && s.background && s.background.selectedRoofId;
  const activePanels = (s && s.panels && s.panels.panels) || [];
  let total = Array.isArray(activePanels) ? activePanels.length : 0;
  Object.keys(roofs).forEach((roofId) => {
    if (String(roofId) === String(selectedRoofId)) return;
    const rp = roofs[roofId] && roofs[roofId].panels;
    if (Array.isArray(rp)) total += rp.length;
  });
  return total;
}

// Order-independent serialization so two configs with the same values but
// different key insertion order hash identically.
function stableStringify(value: any): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
  const keys = Object.keys(value).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
}

// Display-only project_configuration fields whose edits must NOT regenerate
// the BOM (regen wipes user_builds / qty edits). Everything else in the
// persisted config — wind/snow/seismic/building_code/exposure/span/module/
// attachment/etc. — feeds the engineering calc and so invalidates the BOM.
const BOM_SIGNATURE_EXCLUDED_KEYS = new Set<string>(['custom_title', 'city_name', 'project_name']);

// Signature of the BOM-affecting slice of the PUT body's project_configuration.
function bomConfigSignature(projectConfiguration: any): string {
  if (!projectConfiguration) return '';
  const filtered: any = {};
  Object.keys(projectConfiguration).forEach((k) => {
    if (BOM_SIGNATURE_EXCLUDED_KEYS.has(k)) return;
    filtered[k] = projectConfiguration[k];
  });
  return stableStringify(filtered);
}

// Last config signature the BOM in state.bom was generated from, per project.
// Seeded by fetchLiteBom (initial load + every regen-chained fetch) so the
// first user config change after load is detected. Compared in
// updateLiteProject to decide whether an engineering field changed and the
// BOM must be regenerated rather than left stale.
let lastBomSig: { projectId: string; sig: string } | null = null;

export function createLiteProject(action$) {
  return action$.ofType(LiteProjectActionTypes.CREATE_LITE_PROJECT_REQUEST)
    .switchMap((action: any) =>
      ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.CREATE_LITE_PROJECT_SUCCESS),
        onSuccess: [
          CREATE_LITE_PROJECT_SUCCESS,
          FETCH_PROJECT_AFTER_LITE_CREATE,
          REDIRECT_TO_LITE_PROJECT.bind(null, action.payload.productName),
        ],
        onError: CREATE_LITE_PROJECT_FAILURE,
        onErrorMessage: 'Could not create project',
        link: hitApi('post', 'api/v1/ub_lite/project/', action.payload.data),
        socketName: 'project',
      }),
    );
}

// Auto-save PUT. Builds the payload from the current Redux snapshot at
// fire-time (not from the action.payload) so rapid field edits are always
// captured by the latest in-flight save. Kicked off by the container's 5s
// interval or by componentWillUnmount when the user navigates away.
//
// On success, we ALSO fire FETCH_LITE_BOM_REQUEST with the same projectId
// (captured from the request action's payload) so the BOM stays in lock-step
// with the persisted server state. Lite never writes projectId into
// state.projectConfiguration — it lives in the URL's match.params — so the
// chain has to use the closure here, not read from state.
export function updateLiteProject(action$) {
  return action$.ofType(LiteProjectActionTypes.UPDATE_LITE_PROJECT_REQUEST)
    .switchMap((action: any) => {
      const { projectId, regenBom, allowEmptyPanels, configOverride } = action.payload;
      const s = state();
      // configOverride is set only by the full BOM page (CompleteBomPage),
      // which runs on a standalone route that never mounts the workspace —
      // so state.projectConfiguration holds only store defaults (productId
      // solarmount_2, empty env). Reading product/config from that snapshot
      // would corrupt the persisted project. Use the authoritative product
      // identity the page passed in (derived from its URL slug) instead — but
      // only for the BOM regen/fetch chain below, NOT in the PATCH body.
      const productIdNum = configOverride
        ? configOverride.productId
        : (s.projectConfiguration && s.projectConfiguration.productId);
      const productName = productIdNum ? getProductName(productIdNum) : '';
      // allowEmptyPanels is set only by PUTs fired from the full BOM page
      // (CompleteBomPage), where the workspace is unmounted and
      // drawingManager.roofs is cleared. In that case omit an empty `roofs`
      // list so the backend doesn't wipe the project's existing panels.
      //
      // With configOverride, send a minimal partial update carrying ONLY the
      // changed field(s). The backend persists just those columns and leaves
      // the rest of the project intact (see UbLiteProjectViewSet.update's
      // `if not partial` guard). product_id is intentionally omitted so a
      // single-field edit like the domestic_content toggle updates that
      // column alone.
      //
      // We stay on PUT (not PATCH) and signal partial intent via ?partial=true.
      // PATCH adds a new verb to the CORS preflight surface, which the
      // reverse proxy in front of the API doesn't whitelist (it allows the
      // already-used GET/POST/PUT/OPTIONS), so a PATCH preflight is rejected.
      const body = configOverride
        ? { project_configuration: { ...configOverride.fields } }
        : buildLiteUpdatePayload(s, !!allowEmptyPanels);
      if (!configOverride && hasLeftPanelValidationErrors(s)) {
        return Observable.of(UPDATE_LITE_PROJECT_FAILURE());
      }
      // Decide up-front whether this save must regenerate the BOM. The decision
      // inputs (panel count, persisted config signature) are settled before the
      // PUT and are not changed by its response, so computing them here is
      // equivalent to the old post-success branch — but it lets us fold the
      // UserBuild wipe into the PUT itself via ?regen_bom=true instead of firing
      // a separate empty-body POST to api/v1/ub_lite/bom/<id>/. Three triggers,
      // preserving the previous logic:
      //   1. regenBom — explicit caller flag (attachment-type dropdown, full
      //      BOM page field edits via configOverride).
      //   2. panelCountChanged — panels added/removed since the last fetched BOM.
      //   3. configChanged — a BOM-affecting project_configuration field moved,
      //      detected against the seeded signature for this project (so the first
      //      PUT after load and project switches never spuriously wipe).
      const newCount = countLitePanels(s);
      const hasPanels = newCount > 0 || !!allowEmptyPanels;
      const prevCount = (s.bom && s.bom.total_modules) || 0;
      const panelCountChanged = !allowEmptyPanels && newCount !== prevCount;
      const configSig = bomConfigSignature(body.project_configuration);
      const configChanged = lastBomSig !== null
        && lastBomSig.projectId === projectId
        && lastBomSig.sig !== configSig;
      const shouldRegen = hasPanels && (regenBom || panelCountChanged || configChanged);
      // ?partial=true (not PATCH) keeps us inside the CORS preflight whitelist;
      // ?regen_bom=true follows the same query-param convention and tells the
      // backend to delete the project's UserBuild rows in this same request so
      // the chained BOM GET recomputes from scratch.
      const queryParams: string[] = [];
      if (configOverride) queryParams.push('partial=true');
      if (shouldRegen) queryParams.push('regen_bom=true');
      const resource = `api/v1/ub_lite/project/${projectId}/${queryParams.length ? `?${queryParams.join('&')}` : ''}`;
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.UPDATE_LITE_PROJECT_SUCCESS),
        onSuccess: [
          UPDATE_LITE_PROJECT_SUCCESS,
          () => {
            const s2 = state();
            const newCount2 = countLitePanels(s2);
            // Skip the BOM GET when the project has zero panels (fresh project
            // or post-Clear-All). Still dispatch BOM_RESET so any stale parts /
            // accessories from a prior load are wiped. allowEmptyPanels overrides
            // this for non-workspace callers (CompleteBomPage) whose in-memory
            // panel slices are empty by definition.
            if (newCount2 === 0 && !allowEmptyPanels) return BOM_RESET();
            const bomReady = s2.bom && s2.bom.isReady;
            if (shouldRegen) {
              // The PUT already wiped user_builds server-side (?regen_bom=true),
              // so this single GET recomputes the BOM from scratch — no separate
              // POST needed. Forward the persisted config signature so
              // fetchLiteBom seeds the baseline from it rather than re-reading
              // live state, which may already hold a newer edit made while this
              // save was in flight.
              return FETCH_LITE_BOM_REQUEST({ projectId, productId: productName, bomSig: configSig });
            }
            // Only fetch if the BOM has never been loaded yet. No fetch needed
            // when nothing BOM-relevant changed and the BOM is already loaded.
            if (!bomReady) {
              lastBomSig = { projectId, sig: configSig };
              return FETCH_LITE_BOM_REQUEST({ projectId, productId: productName });
            }
            // No BOM action needed — seed the signature if unset (or from another
            // project) so the next config edit is detected, then no-op.
            if (lastBomSig === null || lastBomSig.projectId !== projectId) {
              lastBomSig = { projectId, sig: configSig };
            }
            return EMPTY_APP_ACTION();
          },
        ],
        onError: UPDATE_LITE_PROJECT_FAILURE,
        onErrorMessage: 'Auto-save failed',
        link: hitApi('put', resource, body),
        socketName: 'project',
      });
    });
}

// POST /api/v1/ub_lite/bom/<projectId>/ — user-driven BOM save (qty edits,
// finish swaps, accessory toggles, discount). Request body shape matches
// BOMDeserializer (parts_list / rails_lengths / extra_parameters). Backend
// wipes existing UserBuild rows and bulk-creates the new ones, so the body
// must always carry the FULL parts_list, not a diff. takeUntil collapses
// rapid back-to-back saves so only the latest in-flight save commits.
export function saveLiteBom(action$) {
  return action$.ofType(LiteProjectActionTypes.SAVE_LITE_BOM_REQUEST)
    .switchMap((action: any) => {
      const { projectId, productId, body, chainFetch, bomSig } = action.payload;
      const onSuccess: any = chainFetch
        ? [
          SAVE_LITE_BOM_SUCCESS,
          // After the empty-body POST wipes user_builds, fetch a fresh BOM
          // so the right panel reflects the regenerated parts. Used by the
          // attachment-type regen flow — see updateLiteProject epic's
          // regenBom branch. bomSig (when set) is forwarded so the fetch
          // seeds lastBomSig from the persisted config this regen was based
          // on, not from live state.
          () => FETCH_LITE_BOM_REQUEST({ projectId, productId, bomSig }),
        ]
        : SAVE_LITE_BOM_SUCCESS;
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.SAVE_LITE_BOM_REQUEST),
        onSuccess,
        onError: SAVE_LITE_BOM_FAILURE,
        onErrorMessage: 'Could not save BOM',
        link: hitApi('post', `api/v1/ub_lite/bom/${projectId}/`, body),
      });
    });
}

// GET /api/v1/ub_lite/bom/<projectId>/ — body-less. Reuses the legacy
// FETCH_BOM_PART_LIST_SUCCESS / FETCH_BOM_PART_LIST_ERROR actions on
// success/failure so the response lands in the shared `state.bom` slice
// (same shape as legacy /api/v1/bom/<id>/).
export function fetchLiteBom(action$) {
  return action$.ofType(LiteProjectActionTypes.FETCH_LITE_BOM_REQUEST)
    .switchMap((action: any) => {
      const { projectId, productId, bomSig } = action.payload;
      // Record the signature of the config this BOM reflects as the baseline so
      // updateLiteProject can detect the next BOM-affecting config edit.
      // When the regen chain forwards an explicit bomSig, use it: it is the
      // config that was actually persisted and recalculated. Re-reading live
      // state here would be wrong if the user edited a field while this regen
      // was in flight — the newer value would be baked into the baseline and
      // its own autosave would then skip regeneration, leaving the BOM stale.
      // The initial mount fetch carries no bomSig, where live state == the
      // persisted config, so reading it there is correct.
      if (bomSig !== undefined) {
        lastBomSig = { projectId, sig: bomSig };
      } else {
        try {
          lastBomSig = { projectId, sig: bomConfigSignature(buildLiteUpdatePayload(state()).project_configuration) };
        } catch (e) { /* state not ready — leave prior signature */ }
      }
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.FETCH_LITE_BOM_REQUEST),
        onSuccess: [
          FETCH_BOM_PART_LIST_SUCCESS.bind(null, productId),
          () => SET_BOM_PRODUCT(productId, projectId),
        ],
        onError: FETCH_BOM_PART_LIST_ERROR,
        onErrorMessage: 'Cannot load BOM',
        link: hitApi('get', `api/v1/ub_lite/bom/${projectId}/`),
        socketName: 'project',
      });
    });
}

// POST /api/v1/ub_lite/user_template/. The body the action carries is the
// already-built profile object (product, title, project_config) — see
// buildUserProfilePayload in liteProjectActions.ts. Backend's UserTemplateView
// accepts a single object or a list and injects user_id + is_active server-side.
//
// When projectId is supplied, the projectId is captured in the onSuccess
// binding so the reducer (and any downstream listeners) can correlate the
// newly created profile with the project it was created for. Persisting the
// profile_id ProjectVariable on the project is handled by the caller via a
// follow-up UPDATE_LITE_PROJECT_REQUEST once the SUCCESS payload arrives.
export function saveUserProfile(action$) {
  return action$.ofType(LiteProjectActionTypes.SAVE_USER_PROFILE_REQUEST)
    .switchMap((action: any) => {
      const projectId = action.payload && action.payload.projectId;
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.SAVE_USER_PROFILE_SUCCESS),
        onSuccess: SAVE_USER_PROFILE_SUCCESS.bind(null, projectId),
        onError: SAVE_USER_PROFILE_FAILURE,
        onErrorMessage: 'Could not save profile',
        link: hitApi('post', 'api/v1/ub_lite/user_template/', action.payload.profile),
      });
    });
}

// PUT /api/v1/ub_lite/user_template/<profileId>/. Used when the user opens the
// "Save profile" modal for a project that already has a profile associated and
// keeps the existing name — the backend updates the matching UserTemplate row
// in place instead of creating a new one. profile_id on the project is
// unchanged in this case so no follow-up UPDATE_LITE_PROJECT_REQUEST is needed.
export function updateUserProfile(action$) {
  return action$.ofType(LiteProjectActionTypes.UPDATE_USER_PROFILE_REQUEST)
    .switchMap((action: any) => {
      const { profileId, profile } = action.payload;
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.UPDATE_USER_PROFILE_SUCCESS),
        onSuccess: UPDATE_USER_PROFILE_SUCCESS,
        onError: UPDATE_USER_PROFILE_FAILURE,
        onErrorMessage: 'Could not update profile',
        link: hitApi('put', `api/v1/ub_lite/user_template/${profileId}/`, profile),
      });
    });
}

// DELETE /api/v1/ub_lite/user_template/?id=<profileId>. The backend scopes the
// deletion to request.user, so this only removes the signed-in user's profile.
export function deleteUserProfile(action$) {
  return action$.ofType(LiteProjectActionTypes.DELETE_USER_PROFILE_REQUEST)
    .switchMap((action: any) => {
      const { profileId } = action.payload;
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.DELETE_USER_PROFILE_SUCCESS),
        onSuccess: DELETE_USER_PROFILE_SUCCESS.bind(null, profileId),
        onError: DELETE_USER_PROFILE_FAILURE,
        onErrorMessage: 'Could not delete profile',
        link: hitApi('delete', `api/v1/ub_lite/user_template/?id=${profileId}`),
      });
    });
}

// GET /api/v1/ub_lite/user_template/. Backend filters by request.user.id and
// is_active=True, so the response is the active profile list for the
// signed-in user only — no client-side filtering required.
export function fetchUserProfiles(action$) {
  return action$.ofType(LiteProjectActionTypes.FETCH_USER_PROFILES_REQUEST)
    .switchMap(() =>
      ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.FETCH_USER_PROFILES_SUCCESS),
        onSuccess: FETCH_USER_PROFILES_SUCCESS,
        onError: FETCH_USER_PROFILES_FAILURE,
        onErrorMessage: 'Could not load profiles',
        link: hitApi('get', 'api/v1/ub_lite/user_template/'),
      }),
    );
}

// GET /api/v1/ub_lite/project/<id>/variables/. Returns the project's
// ProjectVariable values as a flat dict. The default FETCH_PROJECT serializer
// strips ProjectVariable rows from the response, so the workspace cannot
// otherwise see profile_id on load.
export function fetchProjectVariables(action$) {
  return action$.ofType(LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_REQUEST)
    .switchMap((action: any) => {
      const { projectId } = action.payload;
      return ObservableAjax({
        takeUntil: action$.ofType(LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_SUCCESS),
        onSuccess: FETCH_PROJECT_VARIABLES_SUCCESS,
        onError: FETCH_PROJECT_VARIABLES_FAILURE,
        onErrorMessage: 'Could not load project variables',
        link: hitApi('get', `api/v1/ub_lite/project/${projectId}/variables/`),
      });
    });
}
